import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { commsStatus, commsConnect, commsInbox, commsDashboard, commsSummary, commsDraftReply, commsEmailAction, getKlaviyoStatus, getKlaviyoFlows, connectKlaviyo, draftKlaviyoFlow, getKlaviyoOverview } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import {
  EnvelopeSimple, PlugsConnected, ShieldWarning, Sparkle, ArrowRight, Archive, ShieldCheck,
  Prohibit, Trash, ChatCircleText, Warning, CurrencyDollar, Users, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CAT_COLOR = {
  "Sales Leads": "bg-emerald-500/15 text-emerald-400", "Support Requests": "bg-amber-500/15 text-amber-300",
  "Customer Orders": "bg-sky-500/15 text-sky-400", "Possible Scam": "bg-red-500/15 text-red-400",
  "Spam": "bg-red-500/10 text-red-400", "Vendor Messages": "bg-purple-500/15 text-purple-300",
  "Invoices": "bg-white/8 text-[#cfcfcf]", "Payment Notifications": "bg-emerald-500/10 text-emerald-400",
  "Shipping Notifications": "bg-sky-500/10 text-sky-300", "Newsletters": "bg-white/6 text-[#9a9a9a]",
  "Marketing Emails": "bg-white/6 text-[#9a9a9a]", "Customer Questions": "bg-white/8 text-[#cfcfcf]",
};

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="card-matte p-3.5" data-testid={`comms-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-1.5 label-caps"><Icon size={12} className={accent ? "brand-text" : "text-[#9a9a9a]"} weight="bold" /> {label}</div>
      <div className="font-head text-xl font-semibold mt-1.5">{value}</div>
    </div>
  );
}

function ConnectPanel({ brand }) {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const [provider, setProvider] = useState("gmail");
  const m = useMutation({
    mutationFn: () => commsConnect({ client_id: activeId, provider, brand_name: brand }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["comms"] }); toast.success("Mailbox connected (test mode) — sample inbox loaded"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Connect failed"),
  });
  return (
    <div className="card-matte p-6 max-w-xl" data-testid="comms-connect-panel">
      <div className="flex items-center gap-2 mb-1"><PlugsConnected size={18} className="brand-text" weight="fill" /><span className="font-head font-semibold">Connect this workspace's mailbox</span></div>
      <p className="text-sm text-[#9a9a9a] mb-4">Pick a provider. Connecting in <span className="text-amber-300 font-semibold">Test Mode</span> loads a realistic sample inbox so AI triage works immediately. Live provider OAuth wiring is pending credentials.</p>
      <div className="flex gap-2 mb-4">
        {["gmail", "microsoft365", "imap"].map((p) => (
          <button key={p} data-testid={`provider-${p}`} onClick={() => setProvider(p)}
            className={`text-sm px-3 py-2 rounded-lg border ${provider === p ? "border-[#6B3DFF]/50 bg-[#6B3DFF]/10 text-white" : "border-white/10 text-[#9a9a9a]"}`}>
            {p === "gmail" ? "Gmail" : p === "microsoft365" ? "Microsoft 365" : "IMAP"}
          </button>
        ))}
      </div>
      <Button data-testid="comms-connect-btn" onClick={() => m.mutate()} disabled={m.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">{m.isPending ? "Connecting…" : "Connect (Test Mode)"}</Button>
      <div className="text-[11px] text-[#7a7a7a] mt-3">Future channels: Messenger, Instagram DM, WhatsApp, SMS, Live Chat, Google Business Messages — all plug into this same inbox.</div>
    </div>
  );
}

function EmailRow({ e }) {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["comms"] });
  const draft = useMutation({ mutationFn: () => commsDraftReply(activeId, e.id), onSuccess: () => { inv(); toast.success("AI reply drafted → Approval Queue (never auto-sent)"); }, onError: (x) => toast.error(x?.response?.data?.detail || "Failed") });
  const act = useMutation({ mutationFn: (a) => commsEmailAction(activeId, e.id, a), onSuccess: (r) => { inv(); toast.success(`Marked ${r.status}`); }, onError: (x) => toast.error(x?.response?.data?.detail || "Failed") });
  const flags = Object.entries(e.flags || {}).filter(([, v]) => v).map(([k]) => k);
  return (
    <div data-testid={`email-${e.id}`} className="p-3.5 rounded-lg bg-white/[0.02] border border-white/8">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{e.subject}</div>
          <div className="text-[11px] text-[#7a7a7a]">{e.from_name} · {e.from_email}</div>
        </div>
        <div className="text-right shrink-0">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${CAT_COLOR[e.category] || "bg-white/8 text-[#cfcfcf]"}`}>{e.category}</span>
          <div className="text-[10px] text-[#7a7a7a] mt-1" data-testid={`confidence-${e.id}`}>AI {Math.round(e.confidence * 100)}%</div>
        </div>
      </div>
      <div className="text-xs text-[#9a9a9a] mt-2 line-clamp-2">{e.body}</div>
      <div className="text-[11px] text-[#7a7a7a] mt-1.5 flex items-center gap-1"><Sparkle size={10} className="brand-text" weight="fill" /> {e.reason}</div>
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {flags.map((f) => <span key={f} className={`text-[10px] px-1.5 py-0.5 rounded ${["spam", "scam", "phishing"].includes(f) ? "bg-red-500/15 text-red-400" : f === "lead" || f === "estimate" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-300"}`}>{f.replace("_", " ")}</span>)}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mt-3">
        <button data-testid={`draft-${e.id}`} onClick={() => draft.mutate()} disabled={draft.isPending} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-[#A56CFF]"><ChatCircleText size={12} /> {e.ai_reply_drafted ? "Re-draft reply" : "Draft AI reply"}</button>
        <button data-testid={`safe-${e.id}`} onClick={() => act.mutate("mark_safe")} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/8 text-[#9a9a9a] hover:text-white"><ShieldCheck size={12} /> Mark safe</button>
        <button data-testid={`archive-${e.id}`} onClick={() => act.mutate("archive")} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/8 text-[#9a9a9a] hover:text-white"><Archive size={12} /> Archive</button>
        <button data-testid={`block-${e.id}`} onClick={() => act.mutate("block_sender")} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/8 text-[#9a9a9a] hover:text-red-400"><Prohibit size={12} /> Block</button>
      </div>
    </div>
  );
}

function KlaviyoPanel() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ["klaviyo-status", activeId], queryFn: () => getKlaviyoStatus(activeId), enabled: !!activeId });
  const flowsQ = useQuery({ queryKey: ["klaviyo-flows", activeId], queryFn: () => getKlaviyoFlows(activeId), enabled: !!activeId });
  const connected = statusQ.data?.connected;
  const testMode = statusQ.data?.test_mode;
  const isLive = connected && !testMode;
  const overviewQ = useQuery({
    queryKey: ["klaviyo-overview", activeId],
    queryFn: () => getKlaviyoOverview(activeId),
    enabled: !!activeId && !!isLive,
  });
  const ov = overviewQ.data;
  const connect = useMutation({
    mutationFn: () => connectKlaviyo({ client_id: activeId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["klaviyo-status", activeId] }); toast.success("Klaviyo connected (test mode) — add a Private API key to go live"); },
    onError: () => toast.error("Connect failed"),
  });
  const draft = useMutation({
    mutationFn: (flow) => draftKlaviyoFlow({ client_id: activeId, flow }),
    onSuccess: () => toast.success("Flow drafted → Approval Queue (never sends without approval)"),
    onError: () => toast.error("Draft failed"),
  });
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <div className="card-matte p-5 mt-6" data-testid="klaviyo-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <EnvelopeSimple size={18} className="brand-text" weight="fill" />
          <span className="font-head font-semibold">Klaviyo Email Automation</span>
          {testMode && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-400">Test Mode</span>}
          {isLive && <span data-testid="klaviyo-live-badge" className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-400">● LIVE</span>}
        </div>
        {connected
          ? <span className="flex items-center gap-1 text-xs text-emerald-400" data-testid="klaviyo-connected"><ShieldCheck size={13} weight="fill" /> {isLive ? (ov?.account?.organization || "Connected") : "Connected"}</span>
          : <Button data-testid="klaviyo-connect-btn" size="sm" onClick={() => connect.mutate()} disabled={connect.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><PlugsConnected size={14} className="mr-1" /> Connect Klaviyo</Button>}
      </div>

      {isLive && (
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="klaviyo-live-stats">
          <div className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#7a7a7a]"><Users size={12} /> Subscribers</div>
            <div className="text-lg font-head font-semibold text-white mt-0.5" data-testid="klaviyo-subscribers">
              {overviewQ.isLoading ? "…" : Number(ov?.subscribers?.total_subscribers || 0).toLocaleString()}
            </div>
          </div>
          <div className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#7a7a7a]"><CurrencyDollar size={12} /> Email rev · 30d</div>
            <div className="text-lg font-head font-semibold text-emerald-400 mt-0.5" data-testid="klaviyo-email-revenue">
              {overviewQ.isLoading ? "…" : (ov?.revenue?.metric_found ? money(ov?.revenue?.email_revenue) : "—")}
            </div>
          </div>
          <div className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#7a7a7a]"><Lightning size={12} /> Campaigns</div>
            <div className="text-lg font-head font-semibold text-white mt-0.5" data-testid="klaviyo-campaigns-count">
              {overviewQ.isLoading ? "…" : (ov?.campaigns?.length || 0)}
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-[#7a7a7a] mb-3">Draft lifecycle email flows in your brand voice. Everything enters the Approval Queue — nothing sends automatically.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {(flowsQ.data || []).map((f) => (
          <div key={f.key} data-testid={`klaviyo-flow-${f.key}`} className="flex items-center justify-between rounded-lg border border-white/8 px-3 py-2.5">
            <div className="min-w-0"><div className="text-sm text-[#cfcfcf]">{f.name}</div><div className="text-[11px] text-[#7a7a7a] truncate">{f.description}</div></div>
            <Button data-testid={`klaviyo-draft-${f.key}`} size="sm" variant="outline" onClick={() => draft.mutate(f.key)} disabled={!connected || draft.isPending}
              className="border-white/15 bg-transparent text-white hover:bg-white/5 shrink-0 ml-2"><Sparkle size={13} className="mr-1 brand-text" weight="fill" /> Draft</Button>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function AiCommsManagerPage() {
  const { activeId, activeClient } = useClient();
  const navigate = useNavigate();
  const [cat, setCat] = useState("all");
  const statusQ = useQuery({ queryKey: ["comms", "status", activeId], queryFn: () => commsStatus(activeId), enabled: !!activeId });
  const connected = statusQ.data?.connected;
  const dashQ = useQuery({ queryKey: ["comms", "dash", activeId], queryFn: () => commsDashboard(activeId), enabled: !!connected });
  const sumQ = useQuery({ queryKey: ["comms", "sum", activeId], queryFn: () => commsSummary(activeId), enabled: !!connected });
  const inboxQ = useQuery({ queryKey: ["comms", "inbox", activeId, cat], queryFn: () => commsInbox(activeId, cat), enabled: !!connected });
  const d = dashQ.data || {};
  const cats = ["all", ...Object.keys(d.categories || {})];

  return (
    <PageContainer>
      <PageHeader eyebrow="AI Command" title="AI Communications Manager"
        subtitle="AI reads, categorizes and flags every email, then drafts replies for your approval. It never sends, deletes or blocks without you. Each workspace uses its own mailbox.">
        <button data-testid="goto-approvals-comms" onClick={() => navigate("/approvals")} className="text-sm brand-text hover:text-[#A56CFF] flex items-center gap-1">Approval Queue <ArrowRight size={14} /></button>
      </PageHeader>

      {!connected ? <ConnectPanel brand={activeClient?.name} /> : (
        <>
          {sumQ.data?.summary && (
            <div className="card-matte p-4 mb-5 flex items-start gap-2" data-testid="comms-summary">
              <Sparkle size={16} className="brand-text mt-0.5 shrink-0" weight="fill" />
              <div className="text-sm text-[#cfcfcf]">{sumQ.data.summary}</div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-5" data-testid="comms-dashboard">
            <Stat label="New" value={d.new || 0} icon={EnvelopeSimple} accent />
            <Stat label="Urgent" value={d.urgent || 0} icon={Warning} accent />
            <Stat label="Waiting Reply" value={d.waiting_reply || 0} icon={ChatCircleText} />
            <Stat label="Leads" value={d.leads || 0} icon={Users} accent />
            <Stat label="Est. Revenue" value={`$${Math.round(d.revenue_opportunity || 0)}`} icon={CurrencyDollar} accent />
            <Stat label="Scam/Phishing" value={d.scam || 0} icon={ShieldWarning} />
            <Stat label="Spam" value={d.spam || 0} icon={ShieldWarning} />
          </div>

          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="comms-categories">
            {cats.map((c) => (
              <button key={c} data-testid={`cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setCat(c)}
                className={`text-xs px-2.5 py-1.5 rounded-lg border ${cat === c ? "border-[#6B3DFF]/50 bg-[#6B3DFF]/10 text-white" : "border-white/10 text-[#9a9a9a]"}`}>
                {c === "all" ? "All" : c}{c !== "all" && d.categories?.[c] ? ` (${d.categories[c]})` : ""}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-3" data-testid="comms-inbox">
            {(inboxQ.data || []).map((e) => <EmailRow key={e.id} e={e} />)}
            {!inboxQ.isLoading && !(inboxQ.data || []).length && <div className="text-sm text-[#7a7a7a]">No emails in this view.</div>}
          </div>
          <div className="flex items-center gap-2 mt-4 text-xs text-[#7a7a7a]"><ShieldCheck size={14} className="brand-text" /> AI drafts only. Replies enter the Approval Queue — nothing sends without your approval. Emails are never permanently deleted.</div>
        </>
      )}
      <KlaviyoPanel />
    </PageContainer>
  );
}
